import { useLayoutEffect, useState, type RefObject } from 'react';

/** `clientWidth`/`scrollWidth` sono interi arrotondati e `letter-spacing` aggiunge un gap
 *  invisibile dopo l'ultimo glifo (0.06em ≈ 0.7px sull'indicatore di turno): una scritta
 *  che visivamente ci sta può dichiarare ~1px di overflow. Fin qui si considera che entri. */
const FIT_TOLERANCE_PX = 1;

/**
 * `true` finché tutto il testo di `ref` entra nella larghezza che flex gli concede.
 *
 * Misurato, non dedotto da una media query (docs/STYLE-GUIDE.md, stessa regola di
 * piece-lod.ts): la player card è larga quanto `.gs-board-column`, che è la colonna
 * intera fino a 600px — e lo spazio che resta alla scritta dipende da quante pedine si
 * sono mangiate e dalla lingua ("Tocca a te" ~75px contro "Du bist dran" ~88px). Una
 * soglia sul viewport non vede niente di tutto questo.
 *
 * Precondizioni CSS sull'elemento osservato:
 *  - `white-space: nowrap` + `overflow: hidden`, altrimenti `scrollWidth` collassa su
 *    `clientWidth` (il testo va semplicemente a capo) e l'overflow è invisibile;
 *  - lo stato "spento" NON deve cambiare il box (`opacity`, mai `display: none`),
 *    altrimenti nasconderlo lo fa entrare, il che lo rimostra, il che lo fa uscire…
 *
 * Niente dead band alla piece-lod: qui il segnale è a UNA SOLA direzione. L'elemento è
 * `flex: 0 1 auto` senza grow, quindi quando entra flex gli dà esattamente la larghezza
 * del suo contenuto e `scrollWidth - clientWidth` vale 0 — mai un valore negativo. Una
 * isteresi del tipo "riaccendi con N px di margine" sarebbe insoddisfacibile e la scritta,
 * una volta spenta, non tornerebbe mai più. E non serve: nascondere non cambia il box,
 * quindi non esiste retroazione da smorzare.
 *
 * @param ref        elemento da misurare
 * @param contentKey cambia quando cambiano il testo o la larghezza dei fratelli di riga;
 *                   forza una rimisura sincrona pre-paint invece di aspettare il
 *                   ResizeObserver, la cui callback arriverebbe un frame dopo.
 */
export function useFitsOnOneLine(ref: RefObject<HTMLElement | null>, contentKey: string): boolean {
  const [fits, setFits] = useState(true);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      const granted = el.clientWidth;
      const needed = el.scrollWidth;
      // Elemento non in layout: jsdom, oppure `display: none` (la modalità focus
      // collassa `.player-card__left`). Meglio nessuna misura che una misura falsa:
      // si tiene la decisione precedente. Nota: riga piena e scritta compressa a zero
      // dà granted 0 ma needed > 0, quindi NON entra in questa guardia.
      if (granted === 0 && needed === 0) return;
      // React fa bail-out se il valore non cambia: nessun re-render inutile.
      setFits(needed - granted <= FIT_TOLERANCE_PX);
    };

    measure();

    if (typeof ResizeObserver === 'undefined') return;
    // Si osserva l'elemento stesso, non il contenitore: è lui il flex item che cede,
    // quindi il suo content box cambia sia quando la card si restringe sia quando
    // crescono le icone accanto — un solo observer copre entrambi i casi, più lo swap
    // del web font.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, contentKey]);

  return fits;
}
